/**
 * «Del i sosiale medier», phase 1 (owner, 2026-10-22): a published banner or announcement made ready to post on
 * Facebook, Instagram and WhatsApp — the picture in the sizes those apps want, and a caption per app.
 *
 * Nothing is posted from here and no social-media login is held by anybody: the staff member downloads the picture,
 * copies the text, or hands both to the phone's own share sheet, and posts from their own account. Posting straight
 * to a Page (Meta's Graph API) is phase 2 and needs Meta's app review; WhatsApp has no API for groups, Status or
 * Channels at all, so it stays the share sheet and a wa.me link.
 *
 * Only PUBLIC content goes out: a banner that has been approved, an ordinary announcement that is up. A janazah is
 * not offered (it names the deceased, and the family decides where that is said), nor a draft, nor anything about a
 * member.
 *
 * Pure: the panel draws the picture on a canvas from layout(), which is the same placement the TV and the panel's
 * preview use (banners.render() + banner-art pieces), worked out in pixels for one size.
 */

/**
 * The three sizes. Width first, as the apps name them. `text`: how much larger the text is drawn than on a 16:9
 * screen of the same width — a banner's sizes are made for a wide TV, where they fill the height; on a square or tall
 * picture read on a phone they would sit in a thin band. The panel shrinks them again if they no longer fit.
 */
const SIZES = {
  square:   { id: 'square',   w: 1080, h: 1080, text: 1.2, label_no: 'Kvadrat 1080×1080', for_no: 'Facebook og Instagram, innlegg' },
  portrait: { id: 'portrait', w: 1080, h: 1350, text: 1.3, label_no: 'Stående 1080×1350', for_no: 'Instagram, innlegg (fyller mest av skjermen)' },
  story:    { id: 'story',    w: 1080, h: 1920, text: 1.4, label_no: 'Story 1080×1920',   for_no: 'Story på Instagram og Facebook, WhatsApp-status' },
};
const SIZE_ORDER = ['square', 'portrait', 'story'];
/**
 * The screens' own shape (banner studio, 2026-10-31): a banner downloaded as a picture, or copied onto a program
 * (Arrangementer) as its picture, at a full HD TV's size. The text at its TV size (`text` 1): what hangs on the wall.
 * Not one of the three social sizes: «Del» keeps offering those.
 */
const SCREEN = { id: 'screen', w: 1920, h: 1080, text: 1, label_no: 'Skjerm 1920×1080', for_no: 'TV-skjermene, og som bilde på et arrangement' };

/**
 * What each app does with a caption. Instagram cuts at 2 200 characters and never makes a link in a caption
 * clickable, so its caption names the website as text; Facebook and WhatsApp get the address.
 */
const NETWORKS = {
  facebook:  { id: 'facebook',  name_no: 'Facebook',  max: 63206, links: true },
  instagram: { id: 'instagram', name_no: 'Instagram', max: 2200,  links: false },
  whatsapp:  { id: 'whatsapp',  name_no: 'WhatsApp',  max: 2000,  links: true },
};
const NETWORK_ORDER = ['facebook', 'instagram', 'whatsapp'];

/** The ground a picture banner is shown on, as the TV and the panel do (never cropped). */
const PICTURE_GROUND = '#0C1310';
/** The JPEG quality the panel exports at. */
const JPEG_QUALITY = 0.92;

/** Text as it may go out: no control characters, at most one blank line in a row, trimmed. */
function clean(s) {
  return String(s == null ? '' : s)
    .replace(/\r\n?/g, '\n')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** "www.example.no" (a host, as mosque.js keeps it) → "https://www.example.no", or null. */
function urlOf(website) {
  const h = clean(website).replace(/^https?:\/\//i, '').replace(/\/+$/, '');
  return h && /^[a-z0-9.-]+\.[a-z]{2,}(\/[^\s]*)?$/i.test(h) ? 'https://' + h : null;
}

/** Cut to `max` characters at a word where one is near, ending in «…». */
function cut(text, max) {
  if (text.length <= max) return { text, truncated: false };
  const room = text.slice(0, max - 1);
  const at = room.lastIndexOf(' ');
  return { text: (at > max * 0.8 ? room.slice(0, at) : room).trimEnd() + '…', truncated: true };
}

/**
 * The caption for one app: title, text, who it is from, and where to read more.
 *   { title, body, mosque: { name, website }, network } → { text, truncated, network }
 * The title and text are what was published — never rewritten, only cut where the app would cut it.
 */
function caption({ title, body, mosque = {}, network = 'facebook' } = {}) {
  const n = NETWORKS[network] || NETWORKS.facebook;
  const t = clean(title), b = clean(body), name = clean(mosque.name);
  const url = urlOf(mosque.website);
  const head = t ? (n.id === 'whatsapp' ? `*${t.replace(/\*/g, '')}*` : t) : '';
  const tail = [];
  if (name) tail.push(`— ${name}`);
  if (url) tail.push(n.links ? url : `Mer på ${url.replace(/^https:\/\//, '')}`);
  const end = tail.join('\n');
  // The tail is kept whole; the text gives way when the app's limit is reached.
  const fixed = [head, end].filter(Boolean).join('\n\n');
  const room = n.max - fixed.length - (fixed ? 2 : 0);
  const middle = b ? cut(b, Math.max(1, room)) : { text: '', truncated: false };
  const text = [head, middle.text, end].filter(Boolean).join('\n\n');
  const whole = cut(text, n.max);
  return { text: whole.text, truncated: middle.truncated || whole.truncated, network: n.id };
}

/** «Åpne i WhatsApp»: WhatsApp's own link, which opens the app (or WhatsApp Web) with the text ready to send. */
function whatsappLink(text) {
  return 'https://wa.me/?text=' + encodeURIComponent(cut(clean(text), NETWORKS.whatsapp.max).text);
}

/**
 * What may be shared. A banner: approved (live now, or scheduled for later). An announcement: an ordinary one that is
 * up. Never a draft, a stopped or taken-down banner, a janazah, or a push.
 */
function shareable(kind, item) {
  if (!item) return false;
  if (kind === 'banner') return item.state === 'live' || item.state === 'scheduled';
  if (kind === 'notice') return item.kind === 'normal' && Boolean(item.active);
  return false;
}

/**
 * An announcement has no banner of its own: it is drawn in the light notice design, its title as the headline and the
 * mosque's short name above it. The full text is in the caption.
 */
function noticeBanner(notice, mosqueShort) {
  return {
    template: 'notice_light', palette: 'ivory',
    values: { eyebrow: clean(mosqueShort).slice(0, 28), headline: clean(notice && notice.title_no) },
  };
}

/** A box given in percent of the banner (banner-art pieceStyle) → pixels in a w×h picture. */
function boxPx(box, w, h) {
  const along = (start, end, size, total) => {
    const s = typeof box[start] === 'number' ? box[start] : null, e = typeof box[end] === 'number' ? box[end] : null;
    const z = typeof box[size] === 'number' ? box[size] : null;
    if (s !== null && z !== null) return [s, z];
    if (e !== null && z !== null) return [100 - e - z, z];
    if (s !== null && e !== null) return [s, 100 - s - e];
    if (s !== null) return [s, 100 - s];
    if (e !== null) return [0, 100 - e];
    return [0, z !== null ? z : 100];
  };
  const [x, bw] = along('left', 'right', 'width', w), [y, bh] = along('top', 'bottom', 'height', h);
  const r = (n) => Math.round(n * 100) / 100;
  return { x: r((x / 100) * w), y: r((y / 100) * h), w: r((bw / 100) * w), h: r((bh / 100) * h) };
}

/** A piece's SVG at a fixed pixel size: an <img> of an SVG needs real dimensions, not 100%. */
function svgAt(svg, w, h) {
  return String(svg).replace(/width="100%" height="100%"/, `width="${Math.round(w)}" height="${Math.round(h)}"`);
}

/**
 * Where everything goes in one picture (`size` = { w, h }), from a banners.render() result:
 *   { bg, pieces: [{ x, y, w, h, svg }], box: { x, y, w, h }, align, gap, layers: [{ text, px, weight, colour, lineHeight, dir, lang }] }
 * The same rules as the preview's CSS: text sizes are percent of the WIDTH (cqw, times the size's `text`); a plain template keeps 10% clear all
 * round and its text to the left; a designed one puts its text, centred, in the space its ornaments leave (`inset`).
 */
function layout(r, size) {
  if (!r || !size) return null;
  const w = size.w, h = size.h, k = size.text || 1;
  const decor = r.decor || null;
  const box = decor
    ? boxPx({ top: decor.inset.top, right: decor.inset.right, bottom: decor.inset.bottom, left: decor.inset.left }, w, h)
    : { x: w * 0.1, y: h * 0.1, w: w * 0.8, h: h * 0.8 };
  return {
    w, h, bg: r.palette.bg, fg: r.palette.fg,
    pieces: decor ? decor.pieces.map((p) => { const b = boxPx(p.box, w, h); return { ...b, svg: svgAt(p.svg, b.w, b.h) }; }) : [],
    box, align: decor ? 'center' : 'left',
    gap: Math.round(w * 0.012 * k),
    layers: r.layers.map((l) => ({
      text: l.text, px: Math.round((l.sizePct / 100) * w * k), weight: l.weight, colour: l.colour,
      lineHeight: Number(l.lineHeight) || 1.25, dir: l.dir || 'ltr', lang: l.lang || null,
    })),
  };
}

/** A picture of iw×ih placed whole in w×h (never cropped), centred: { x, y, w, h }. */
function contain(iw, ih, w, h) {
  if (!(iw > 0 && ih > 0)) return { x: 0, y: 0, w, h };
  const k = Math.min(w / iw, h / ih);
  const dw = Math.round(iw * k), dh = Math.round(ih * k);
  return { x: Math.round((w - dw) / 2), y: Math.round((h - dh) / 2), w: dw, h: dh };
}

/**
 * Lines of `text` no wider than `max`, by `measure(s)` (the canvas's measureText). Breaks at spaces; a word longer
 * than a line is broken where it has to be (the preview's overflow-wrap:anywhere). Typed line breaks are kept.
 */
function wrap(text, max, measure) {
  const out = [];
  for (const para of String(text).split('\n')) {
    let line = '';
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const next = line ? line + ' ' + word : word;
      if (measure(next) <= max) { line = next; continue; }
      if (line) out.push(line);
      line = '';
      let rest = word;
      while (measure(rest) > max && rest.length > 1) {
        let i = rest.length - 1;
        while (i > 1 && measure(rest.slice(0, i)) > max) i--;
        out.push(rest.slice(0, i)); rest = rest.slice(i);
      }
      line = rest;
    }
    out.push(line);
  }
  return out;
}

/** A file name a phone and a PC both keep: «banner-eid-mubarak-1080x1350.jpg». */
function fileName(title, sizeId, prefix = 'banner') {
  const s = SIZES[sizeId] || SIZES.square;
  const slug = clean(title).toLowerCase()
    .replace(/æ/g, 'ae').replace(/ø/g, 'o').replace(/å/g, 'a')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '');
  return `${prefix}${slug ? '-' + slug : ''}-${s.w}x${s.h}.jpg`;
}

module.exports = {
  SIZES, SIZE_ORDER, SCREEN, NETWORKS, NETWORK_ORDER, PICTURE_GROUND, JPEG_QUALITY,
  caption, whatsappLink, shareable, noticeBanner, layout, contain, wrap, fileName, boxPx, urlOf,
};
